
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BookingWithFinancials } from '@/hooks/useBookingCore';
import { useBookingMutations } from '@/hooks/useBookingMutations';
import { Plus, CreditCard, Wallet, DollarSign, Undo2, AlertTriangle, ChevronDown, ChevronRight, History, Download, FileSpreadsheet, FileText, FileType, CheckCircle2, Pencil, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import * as XLSX from 'xlsx';
import { generateInvoicePDF } from '@/utils/invoicePdfGenerator';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { supabase } from '@/integrations/supabase/client';
import { NightlyBreakdownPanel } from './NightlyBreakdownPanel';

interface BookingFinancialsProps {
  booking: BookingWithFinancials;
  onUpdate: () => Promise<void>;
}

export const BookingFinancials: React.FC<BookingFinancialsProps> = ({ booking, onUpdate }) => {
  const { addPayment, updatePayment, deletePayment } = useBookingMutations();
  const { toast } = useToast();
  const { currentHotel } = useOptimizedHotel();
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState<{
    amount: number | '';
    payment_method: string;
    payment_type: string;
    transaction_id: string;
    notes: string;
  }>({
    amount: '',
    payment_method: 'cash',
    payment_type: 'payment',
    transaction_id: '',
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [refundConfirmation, setRefundConfirmation] = useState<any>(null);
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [refundReason, setRefundReason] = useState<string>('');
  const [expandedPayments, setExpandedPayments] = useState<Set<string>>(new Set());
  const [editingRefund, setEditingRefund] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<any>(null);

  const nights = useMemo(() => {
    const checkIn = new Date(booking.check_in_date);
    const checkOut = new Date(booking.check_out_date);
    const diffTime = checkOut.getTime() - checkIn.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }, [booking.check_in_date, booking.check_out_date]);

  const handleAddPayment = async () => {
    try {
      setIsLoading(true);
      await addPayment(booking.id, {
        amount: typeof paymentForm.amount === 'number' ? paymentForm.amount : 0,
        payment_method: paymentForm.payment_method,
        payment_type: paymentForm.payment_type,
        transaction_id: paymentForm.transaction_id || undefined,
        notes: paymentForm.notes || undefined
      });
      setIsAddingPayment(false);
      setPaymentForm({ 
        amount: '',
        payment_method: 'cash', 
        payment_type: 'payment', 
        transaction_id: '',
        notes: '' 
      });
      
      // ⚡ RPC уже обновляет balance на сервере — просто вызываем onUpdate
      await onUpdate();
    } catch (error) {
      console.error('Error adding payment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    const methods = {
      'cash': 'Наличные',
      'card': 'Карта',
      'transfer': 'Перевод',
      'online': 'Онлайн'
    };
    return methods[method as keyof typeof methods] || method;
  };

  const getPaymentTypeLabel = (type: string) => {
    const types = {
      'prepayment': 'Предоплата',
      'payment': 'Оплата',
      'refund': 'Возврат'
    };
    return types[type as keyof typeof types] || type;
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusMap = {
      'pending': { label: 'Ожидает', variant: 'outline' as const },
      'completed': { label: 'Завершен', variant: 'default' as const },
      'failed': { label: 'Ошибка', variant: 'destructive' as const },
      'cancelled': { label: 'Отменен', variant: 'secondary' as const },
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, variant: 'outline' as const };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const getBalanceColor = (balance: number) => {
    if (balance > 0) return 'text-destructive';
    if (balance < 0) return 'text-green-600';
    return 'text-muted-foreground';
  };

  const handleRefund = async (payment: any) => {
    if (payment.payment_type === 'refund') {
      toast({
        title: "Ошибка",
        description: "Нельзя сделать возврат для возврата",
        variant: "destructive",
      });
      return;
    }

    // Вычисляем доступную сумму для возврата
    const refunds = booking.payments?.filter(
      p => p.payment_type === 'refund' && 
      p.notes?.includes(payment.id) || p.transaction_id?.includes(payment.id)
    ) || [];
    
    const totalRefunded = refunds.reduce((sum, r) => sum + r.amount, 0);
    const availableAmount = payment.amount - totalRefunded;

    if (availableAmount <= 0) {
      toast({
        title: "Ошибка",
        description: "Этот платеж уже полностью возвращен",
        variant: "destructive",
      });
      return;
    }

    // Открываем диалог добавления платежа с предвыбранным типом "refund"
    setPaymentForm({
      amount: availableAmount,
      payment_method: payment.payment_method,
      payment_type: 'refund',
      transaction_id: `REFUND_${payment.transaction_id || payment.id}`,
      notes: `Возврат платежа от ${new Date(payment.payment_date).toLocaleDateString('ru-RU')}`
    });
    setIsAddingPayment(true);
  };

  const handleEditRefund = (refund: any) => {
    // Parse reason from notes
    const reasonMatch = refund.notes?.match(/Причина:\s*(.+)$/);
    const reason = reasonMatch ? reasonMatch[1] : '';
    
    setRefundAmount(refund.amount.toString());
    setRefundReason(reason);
    setIsEditMode(true);
    setEditingRefund(refund);
    setRefundConfirmation(refund);
  };

  const handleDeleteRefund = (refund: any) => {
    setDeleteConfirmation(refund);
  };

  const confirmDeleteRefund = async () => {
    const refund = deleteConfirmation;
    if (!refund) return;

    try {
      setIsLoading(true);
      await deletePayment(refund.id);
      
      toast({
        title: "Успешно",
        description: `Возврат на сумму ${refund.amount} ₽ удален`,
      });
      
      setDeleteConfirmation(null);
      // ⚡ Хук deletePayment уже инвалидирует кеш — просто вызываем onUpdate
      await onUpdate();
    } catch (error) {
      console.error('Error deleting refund:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAvailableRefundAmount = (payment: any) => {
    const refunds = booking.payments?.filter(
      p => p.payment_type === 'refund' && 
      (p.notes?.includes(payment.id) || p.transaction_id?.includes(payment.id))
    ) || [];
    
    const totalRefunded = refunds.reduce((sum, r) => sum + r.amount, 0);
    return payment.amount - totalRefunded;
  };

  const getPaymentRefunds = (payment: any) => {
    return booking.payments?.filter(
      p => p.payment_type === 'refund' && 
      (p.notes?.includes(payment.id) || p.transaction_id?.includes(payment.id))
    ) || [];
  };

  const togglePaymentExpansion = (paymentId: string) => {
    setExpandedPayments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(paymentId)) {
        newSet.delete(paymentId);
      } else {
        newSet.add(paymentId);
      }
      return newSet;
    });
  };

  const exportToCSV = () => {
    if (!booking.payments || booking.payments.length === 0) {
      toast({
        title: "Ошибка",
        description: "Нет данных для экспорта",
        variant: "destructive",
      });
      return;
    }

    // Подготовка данных
    const csvData = booking.payments.map(payment => ({
      'Дата': new Date(payment.payment_date).toLocaleString('ru-RU'),
      'Сумма (₽)': payment.payment_type === 'refund' ? -payment.amount : payment.amount,
      'Способ оплаты': getPaymentMethodLabel(payment.payment_method),
      'Тип': getPaymentTypeLabel(payment.payment_type),
      'Статус': payment.status === 'completed' ? 'Завершен' : payment.status === 'pending' ? 'Ожидает' : payment.status,
      'ID транзакции': payment.transaction_id || '',
      'Комментарий': payment.notes || ''
    }));

    // Конвертация в CSV
    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => 
        headers.map(header => {
          const value = row[header as keyof typeof row];
          // Экранирование значений с запятыми и кавычками
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // Добавляем BOM для корректного отображения кириллицы в Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `Платежи_${booking.booking_reference || booking.id}_${new Date().toLocaleDateString('ru-RU').replace(/\./g, '-')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Успешно",
      description: "История платежей экспортирована в CSV",
    });
  };

  const exportToExcel = () => {
    if (!booking.payments || booking.payments.length === 0) {
      toast({
        title: "Ошибка",
        description: "Нет данных для экспорта",
        variant: "destructive",
      });
      return;
    }

    // Подготовка данных для Excel
    const excelData = booking.payments.map(payment => {
      const refunds = getPaymentRefunds(payment);
      const availableAmount = getAvailableRefundAmount(payment);
      const totalRefunded = payment.amount - availableAmount;

      return {
        'Дата': new Date(payment.payment_date).toLocaleString('ru-RU'),
        'Сумма (₽)': payment.payment_type === 'refund' ? -payment.amount : payment.amount,
        'Способ оплаты': getPaymentMethodLabel(payment.payment_method),
        'Тип': getPaymentTypeLabel(payment.payment_type),
        'Статус': payment.status === 'completed' ? 'Завершен' : payment.status === 'pending' ? 'Ожидает' : payment.status,
        'ID транзакции': payment.transaction_id || '',
        'Комментарий': payment.notes || '',
        'Возвратов': refunds.length,
        'Сумма возвратов (₽)': totalRefunded > 0 ? totalRefunded : '',
        'Доступно для возврата (₽)': payment.payment_type !== 'refund' && availableAmount > 0 ? availableAmount : ''
      };
    });

    // Создание листа с заголовком
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Добавление информации о бронировании в начало
    XLSX.utils.sheet_add_aoa(ws, [
      ['История платежей'],
      ['Бронирование:', booking.booking_reference || booking.id],
      ['Гость:', booking.guest_name || ''],
      ['Дата экспорта:', new Date().toLocaleString('ru-RU')],
      [''],
      ['Общая сумма:', booking.total_amount, '₽'],
      ['Оплачено:', booking.financial?.paid_amount || 0, '₽'],
      ['Баланс:', booking.financial?.balance_amount || 0, '₽'],
      ['']
    ], { origin: 'A1' });

    // Настройка ширины столбцов
    ws['!cols'] = [
      { wch: 20 }, // Дата
      { wch: 12 }, // Сумма
      { wch: 15 }, // Способ
      { wch: 12 }, // Тип
      { wch: 12 }, // Статус
      { wch: 25 }, // ID
      { wch: 40 }, // Комментарий
      { wch: 10 }, // Возвратов
      { wch: 18 }, // Сумма возвратов
      { wch: 20 }  // Доступно
    ];

    // Создание книги
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Платежи');

    // Сохранение файла
    XLSX.writeFile(wb, `Платежи_${booking.booking_reference || booking.id}_${new Date().toLocaleDateString('ru-RU').replace(/\./g, '-')}.xlsx`);

    toast({
      title: "Успешно",
      description: "История платежей экспортирована в Excel",
    });
  };

  const confirmEditRefund = async () => {
    const refund = editingRefund;
    if (!refund) return;

    const refundAmountNum = parseFloat(refundAmount);

    // Validation
    if (!refundAmount || isNaN(refundAmountNum) || refundAmountNum <= 0) {
      toast({
        title: "Ошибка",
        description: "Сумма возврата должна быть больше 0",
        variant: "destructive",
      });
      return;
    }

    if (!refundReason || refundReason.trim().length === 0) {
      toast({
        title: "Ошибка",
        description: "Необходимо указать причину возврата",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Get parent payment date
      const parentPayment = booking.payments?.find(
        p => p.id === refund.transaction_id?.replace('REFUND_', '') || 
        refund.notes?.includes(p.id)
      );
      const paymentDate = parentPayment?.payment_date || refund.payment_date;
      
      const notes = `Возврат платежа от ${new Date(paymentDate).toLocaleDateString()}. Причина: ${refundReason}`;
      
      await updatePayment(refund.id, {
        amount: refundAmountNum,
        notes
      });
      
      toast({
        title: "Успешно",
        description: `Возврат обновлен на сумму ${refundAmountNum} ₽`,
      });
      
      setRefundConfirmation(null);
      setRefundAmount('');
      setRefundReason('');
      setIsEditMode(false);
      setEditingRefund(null);
      await onUpdate();
    } catch (error) {
      console.error('Error updating refund:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmRefund = async () => {
    // If in edit mode, use edit handler instead
    if (isEditMode) {
      return confirmEditRefund();
    }

    const payment = refundConfirmation;
    if (!payment) return;

    const availableAmount = getAvailableRefundAmount(payment);
    const refundAmountNum = parseFloat(refundAmount);

    // Валидация суммы возврата
    if (!refundAmount || isNaN(refundAmountNum) || refundAmountNum <= 0) {
      toast({
        title: "Ошибка",
        description: "Сумма возврата должна быть больше 0",
        variant: "destructive",
      });
      return;
    }

    if (refundAmountNum > availableAmount) {
      toast({
        title: "Ошибка",
        description: `Сумма возврата не может превышать доступную сумму (${availableAmount} ₽)`,
        variant: "destructive",
      });
      return;
    }

    // Валидация причины возврата
    if (!refundReason || refundReason.trim().length === 0) {
      toast({
        title: "Ошибка",
        description: "Необходимо указать причину возврата",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      const notes = refundReason 
        ? `Возврат платежа от ${new Date(payment.payment_date).toLocaleDateString()}. Причина: ${refundReason}`
        : `Возврат платежа от ${new Date(payment.payment_date).toLocaleDateString()}`;
      
      await addPayment(booking.id, {
        amount: refundAmountNum,
        payment_method: payment.payment_method,
        payment_type: 'refund',
        transaction_id: `REFUND_${payment.transaction_id || payment.id}`,
        notes
      });
      
      toast({
        title: "Успешно",
        description: `Возврат на сумму ${refundAmountNum} ₽ создан`,
      });
      setRefundConfirmation(null);
      setRefundAmount('');
      setRefundReason('');
      setIsEditMode(false);
      setEditingRefund(null);
      await onUpdate();
    } catch (error) {
      console.error('Error processing refund:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!currentHotel) {
      toast({
        title: "Ошибка",
        description: "Информация об отеле не найдена",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Получаем информацию об отеле
      const { data: hotelData, error: hotelError } = await supabase
        .from('hotels')
        .select('*')
        .eq('id', currentHotel.id)
        .single();

      if (hotelError || !hotelData) {
        throw new Error('Не удалось получить информацию об отеле');
      }

      // Подготавливаем данные отеля для счета
      const hotelInfo = {
        name: hotelData.name,
        address: hotelData.address_full || undefined,
        phone: (hotelData as any).contact_phone || undefined,
        email: (hotelData as any).contact_email || undefined,
        inn: (hotelData as any).inn || undefined,
        kpp: (hotelData as any).kpp || undefined,
        ogrn: (hotelData as any).ogrn || undefined,
        bank_name: (hotelData as any).bank_name || undefined,
        bank_account: (hotelData as any).bank_account || undefined,
        correspondent_account: (hotelData as any).correspondent_account || undefined,
        bik: (hotelData as any).bik || undefined,
      };

      await generateInvoicePDF(booking, hotelInfo);

      toast({
        title: "✅ Счет сформирован",
        description: "PDF файл загружен успешно"
      });
    } catch (error) {
      console.error('Ошибка генерации счета:', error);
      toast({
        title: "Ошибка",
        description: "Не удалось сформировать счет",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* AK-47 v8: Покомпонентный snapshot (Auto/Manual/Locked) */}
      <NightlyBreakdownPanel booking={booking} onUpdate={onUpdate} />

      {/* Финансовая сводка */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Финансовая информация
            </CardTitle>
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleDownloadInvoice}
              disabled={isLoading}
              className="gap-2"
            >
              <FileType className="h-4 w-4" />
              Скачать счет PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            // 🔥 AK-47: Рассчитываем АКТУАЛЬНЫЕ финансовые данные из платежей
            const payments = booking.payments || [];
            const actualPaid = payments
              .filter(p => p.payment_type !== 'refund' && p.status === 'completed')
              .reduce((sum, p) => sum + p.amount, 0);
            const actualRefunded = payments
              .filter(p => p.payment_type === 'refund' && p.status === 'completed')
              .reduce((sum, p) => sum + p.amount, 0);
            
            // Чистая оплаченная сумма = платежи - возвраты
            const netPaid = actualPaid - actualRefunded;
            // Баланс = общая сумма - чистая оплата
            const actualBalance = booking.total_amount - netPaid;
            // Статус оплаты
            const paymentStatus = actualBalance <= 0 ? 'paid' : netPaid > 0 ? 'partial' : 'pending';
            
            return (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{booking.total_amount} ₽</div>
                  <div className="text-sm text-muted-foreground">Общая сумма</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{netPaid} ₽</div>
                  <div className="text-sm text-muted-foreground">
                    Оплачено {actualRefunded > 0 && <span className="text-destructive text-xs">(-{actualRefunded}₽ возврат)</span>}
                  </div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className={`text-2xl font-bold ${getBalanceColor(actualBalance)}`}>
                    {actualBalance} ₽
                  </div>
                  <div className="text-sm text-muted-foreground">Баланс</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <Badge variant={paymentStatus === 'paid' ? 'default' : 'outline'}>
                    {paymentStatus === 'paid' ? 'Оплачено' : 
                     paymentStatus === 'partial' ? 'Частично' : 'Не оплачено'}
                  </Badge>
                  <div className="text-sm text-muted-foreground mt-1">Статус</div>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* История платежей */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              История платежей
            </span>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" disabled={!booking.payments || booking.payments.length === 0}>
                    <Download className="h-4 w-4 mr-2" />
                    Экспорт
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-background z-50">
                  <DropdownMenuItem onClick={exportToExcel} className="cursor-pointer">
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                    Экспорт в Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToCSV} className="cursor-pointer">
                    <FileText className="h-4 w-4 mr-2 text-blue-600" />
                    Экспорт в CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Dialog open={isAddingPayment} onOpenChange={(open) => {
                setIsAddingPayment(open);
                if (open) {
                  // При открытии диалога устанавливаем РЕАЛЬНЫЙ остаток к оплате.
                  // Источник истины — тот же расчёт, что показан в карточке «Баланс» выше:
                  // balance = total_amount − (платежи − возвраты). Это гарантирует, что
                  // в поле «Сумма» подставится именно остаток, а не полная сумма брони.
                  const payments = booking.payments || [];
                  const paid = payments
                    .filter(p => p.payment_type !== 'refund' && p.status === 'completed')
                    .reduce((sum, p) => sum + p.amount, 0);
                  const refunded = payments
                    .filter(p => p.payment_type === 'refund' && p.status === 'completed')
                    .reduce((sum, p) => sum + p.amount, 0);
                  const netPaid = paid - refunded;
                  const balance =
                    booking.financial?.balance_amount
                    ?? (booking as any).balance_amount
                    ?? ((booking.total_amount || 0) - netPaid);
                  setPaymentForm(prev => ({
                    ...prev,
                    amount: Math.max(0, balance)
                  }));
                }
              }}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Добавить платеж
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Добавить платеж</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="amount">Сумма</Label>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        value={paymentForm.amount === 0 ? '' : paymentForm.amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          setPaymentForm({ ...paymentForm, amount: value === '' ? '' : parseFloat(value) || 0 });
                        }}
                      />
                    </div>
                    <div>
                      <Label htmlFor="payment_method">Способ оплаты</Label>
                      <Select value={paymentForm.payment_method} onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Наличные</SelectItem>
                          <SelectItem value="card">Карта</SelectItem>
                          <SelectItem value="transfer">Перевод</SelectItem>
                          <SelectItem value="online">Онлайн</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="payment_type">Тип платежа</Label>
                      <Select value={paymentForm.payment_type} onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_type: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="prepayment">Предоплата</SelectItem>
                          <SelectItem value="payment">Доплата</SelectItem>
                          <SelectItem value="refund">Возврат</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="transaction_id">ID транзакции</Label>
                      <Input
                        id="transaction_id"
                        value={paymentForm.transaction_id}
                        onChange={(e) => setPaymentForm({ ...paymentForm, transaction_id: e.target.value })}
                        placeholder="Необязательно"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="notes">Комментарий</Label>
                    <Textarea
                      id="notes"
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                      placeholder="Дополнительная информация о платеже..."
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsAddingPayment(false)}>
                      Отмена
                    </Button>
                    <Button onClick={handleAddPayment} disabled={isLoading || paymentForm.amount === '' || paymentForm.amount <= 0}>
                      {isLoading ? 'Добавление...' : 'Добавить'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Дата</TableHead>
                <TableHead>Сумма</TableHead>
                <TableHead>Способ</TableHead>
                <TableHead>Тип</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>ID транзакции</TableHead>
                <TableHead>Комментарий</TableHead>
                <TableHead>Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {booking.payments?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">
                    Платежи не найдены
                  </TableCell>
                </TableRow>
              ) : (
                booking.payments?.map((payment) => {
                  const refunds = getPaymentRefunds(payment);
                  const hasRefunds = refunds.length > 0;
                  const isExpanded = expandedPayments.has(payment.id);
                  const availableAmount = getAvailableRefundAmount(payment);
                  const totalRefunded = payment.amount - availableAmount;
                  const refundPercentage = payment.payment_type !== 'refund' 
                    ? (totalRefunded / payment.amount) * 100 
                    : 0;

                  return (
                    <React.Fragment key={payment.id}>
                      <TableRow className={cn(
                        payment.payment_type === 'refund' && "bg-destructive/5",
                        hasRefunds && "border-b-0"
                      )}>
                        <TableCell>
                          {payment.payment_type !== 'refund' && hasRefunds && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => togglePaymentExpansion(payment.id)}
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(payment.payment_date).toLocaleString('ru-RU')}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className={payment.payment_type === 'refund' ? 'text-destructive font-medium' : 'text-green-600 font-medium'}>
                              {payment.payment_type === 'refund' ? '-' : '+'}{payment.amount} ₽
                            </div>
                            {payment.payment_type !== 'refund' && hasRefunds && (
                              <div className="text-xs text-muted-foreground">
                                Возвращено: {totalRefunded} ₽ ({refundPercentage.toFixed(0)}%)
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {getPaymentMethodLabel(payment.payment_method)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getPaymentTypeLabel(payment.payment_type)}
                            {payment.payment_type !== 'refund' && hasRefunds && (
                              <Badge variant="outline" className="text-xs">
                                <History className="h-3 w-3 mr-1" />
                                {refunds.length}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {getPaymentStatusBadge(payment.status)}
                        </TableCell>
                        <TableCell>
                          {payment.transaction_id || '-'}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {payment.notes || '-'}
                        </TableCell>
                        <TableCell>
                          {payment.payment_type !== 'refund' && availableAmount > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRefund(payment)}
                              disabled={isLoading}
                              className="text-destructive hover:text-destructive"
                            >
                              <Undo2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* История возвратов */}
                      {payment.payment_type !== 'refund' && hasRefunds && isExpanded && (
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={9} className="p-0">
                            <div className="px-12 py-3 space-y-2">
                              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
                                <History className="h-4 w-4" />
                                История возвратов
                              </div>
                              <div className="space-y-2">
                                 {refunds.map((refund) => (
                                  <div
                                    key={refund.id}
                                    className="flex items-center justify-between bg-background p-3 rounded-lg border border-destructive/20"
                                  >
                                    <div className="flex items-center gap-4 flex-1">
                                      <div className="flex items-center gap-2">
                                        <Undo2 className="h-4 w-4 text-destructive" />
                                        <span className="font-medium text-destructive">
                                          -{refund.amount} ₽
                                        </span>
                                      </div>
                                      <div className="text-sm text-muted-foreground">
                                        {new Date(refund.payment_date).toLocaleString('ru-RU')}
                                      </div>
                                      {refund.notes && (
                                        <div className="text-sm text-muted-foreground max-w-md truncate">
                                          {refund.notes}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="text-destructive border-destructive">
                                        {getPaymentStatusBadge(refund.status)}
                                      </Badge>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditRefund(refund)}
                                        disabled={isLoading}
                                        className="h-8 w-8 p-0"
                                        title="Редактировать возврат"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteRefund(refund)}
                                        disabled={isLoading}
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                        title="Удалить возврат"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Прогресс бар возвратов */}
                              <div className="mt-3 pt-3 border-t">
                                <div className="flex items-center justify-between text-sm mb-2">
                                  <span className="text-muted-foreground">Прогресс возврата:</span>
                                  <span className="font-medium">
                                    {totalRefunded} / {payment.amount} ₽ ({refundPercentage.toFixed(1)}%)
                                  </span>
                                </div>
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-destructive transition-all duration-300"
                                    style={{ width: `${Math.min(refundPercentage, 100)}%` }}
                                  />
                                </div>
                                {availableAmount > 0 && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    Доступно для возврата: {availableAmount} ₽
                                  </div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Диалог подтверждения возврата */}
      <AlertDialog open={!!refundConfirmation} onOpenChange={(open) => {
        if (!open) {
          setRefundConfirmation(null);
          setIsEditMode(false);
          setEditingRefund(null);
          setRefundAmount('');
          setRefundReason('');
        }
      }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className={cn(
              "flex items-center gap-2",
              isEditMode && "text-primary"
            )}>
              {isEditMode ? (
                <>
                  <Pencil className="h-5 w-5" />
                  Редактирование возврата
                </>
              ) : (
                <>
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  Возврат средств
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 pt-4">
                {refundConfirmation && (
                  <>
                    <div className="bg-muted p-4 rounded-lg space-y-3">
                      {!isEditMode && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Оригинальная сумма:</span>
                            <span className="font-bold">{refundConfirmation.amount} ₽</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Доступно для возврата:</span>
                            <span className="font-bold text-green-600">
                              {getAvailableRefundAmount(refundConfirmation)} ₽
                            </span>
                          </div>
                        </>
                      )}
                      {isEditMode && (
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Текущая сумма возврата:</span>
                          <span className="font-bold">{editingRefund.amount} ₽</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Способ оплаты:</span>
                        <span>{getPaymentMethodLabel(refundConfirmation.payment_method)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Дата платежа:</span>
                        <span className="text-sm">{new Date(refundConfirmation.payment_date).toLocaleString('ru-RU')}</span>
                      </div>
                      {refundConfirmation.transaction_id && (
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">ID транзакции:</span>
                          <span className="font-mono text-xs">{refundConfirmation.transaction_id}</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 bg-background/50 p-4 rounded-lg border-2 border-primary/20">
                      <Label htmlFor="refundAmount" className="text-foreground font-semibold text-base flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Сумма возврата *
                      </Label>
                      <div className="relative">
                        <Input
                          id="refundAmount"
                          type="number"
                          step="0.01"
                          min="0.01"
                          max={isEditMode ? undefined : getAvailableRefundAmount(refundConfirmation)}
                          value={refundAmount}
                          onChange={(e) => setRefundAmount(e.target.value)}
                          placeholder="Введите сумму возврата"
                          className={cn(
                            "text-2xl font-bold h-14 pr-12 bg-background transition-colors",
                            parseFloat(refundAmount) > getAvailableRefundAmount(refundConfirmation) && "border-destructive focus-visible:ring-destructive",
                            parseFloat(refundAmount) > 0 && parseFloat(refundAmount) <= getAvailableRefundAmount(refundConfirmation) && "border-green-500 focus-visible:ring-green-500"
                          )}
                          autoFocus
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xl font-bold text-muted-foreground">
                          ₽
                        </div>
                      </div>
                      
                      {/* Валидация суммы */}
                      {!isEditMode && parseFloat(refundAmount) > getAvailableRefundAmount(refundConfirmation) && (
                        <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                          <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                          <div className="space-y-1">
                            <p className="text-sm font-semibold text-destructive">
                              ⚠️ Сумма превышает доступную для возврата
                            </p>
                            <p className="text-xs text-destructive/80">
                              Максимальная сумма: {getAvailableRefundAmount(refundConfirmation)} ₽
                              <br />
                              Превышение: {(parseFloat(refundAmount) - getAvailableRefundAmount(refundConfirmation)).toFixed(2)} ₽
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {!isEditMode && parseFloat(refundAmount) > 0 && parseFloat(refundAmount) <= getAvailableRefundAmount(refundConfirmation) && (
                        <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-green-900 dark:text-green-100">
                              ✓ Корректная сумма возврата
                            </p>
                            <p className="text-xs text-green-700 dark:text-green-300">
                              Останется для возврата: {(getAvailableRefundAmount(refundConfirmation) - parseFloat(refundAmount)).toFixed(2)} ₽
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {!isEditMode && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Доступно для возврата:</span>
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-primary font-medium"
                            onClick={() => setRefundAmount(getAvailableRefundAmount(refundConfirmation).toString())}
                          >
                            Заполнить полностью ({getAvailableRefundAmount(refundConfirmation)} ₽)
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 bg-background/50 p-4 rounded-lg border border-border">
                      <Label htmlFor="refundReason" className="text-foreground font-semibold flex items-center gap-2">
                        <History className="h-4 w-4" />
                        Причина возврата *
                      </Label>
                      <Textarea
                        id="refundReason"
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        placeholder="Обязательно укажите причину возврата (например: отмена по инициативе гостя, возврат залога и т.д.)"
                        rows={4}
                        className="resize-none bg-background"
                      />
                      {!refundReason && (
                        <p className="text-xs text-destructive font-medium">
                          ⚠️ Причина возврата обязательна для заполнения
                        </p>
                      )}
                    </div>

                    {parseFloat(refundAmount) > 0 && !isEditMode && (
                      <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 p-3 rounded-lg">
                        <p className="text-sm text-blue-900 dark:text-blue-100">
                          💰 После возврата баланс бронирования изменится на <strong>{refundAmount} ₽</strong>
                        </p>
                      </div>
                    )}

                    {isEditMode && parseFloat(refundAmount) > 0 && parseFloat(refundAmount) !== editingRefund.amount && (
                      <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 p-3 rounded-lg">
                        <p className="text-sm text-blue-900 dark:text-blue-100">
                          💰 Изменение суммы: <strong>{editingRefund.amount} ₽</strong> → <strong>{refundAmount} ₽</strong>
                          <br />
                          Разница: <strong>{(parseFloat(refundAmount) - editingRefund.amount).toFixed(2)} ₽</strong>
                        </p>
                      </div>
                    )}

                    <div className="bg-destructive/10 border border-destructive/20 p-3 rounded-lg">
                      <p className="text-sm text-destructive">
                        ⚠️ Это действие {isEditMode ? 'обновит запись о возврате' : 'создаст запись о возврате'} и пересчитает финансовые показатели бронирования.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmRefund();
              }}
              disabled={
                isLoading || 
                !refundAmount || 
                parseFloat(refundAmount) <= 0 || 
                !refundReason?.trim() ||
                (!isEditMode && parseFloat(refundAmount) > getAvailableRefundAmount(refundConfirmation))
              }
              className={cn(
                isEditMode 
                  ? "bg-primary hover:bg-primary/90" 
                  : "bg-destructive hover:bg-destructive/90"
              )}
            >
              {isLoading 
                ? 'Обработка...' 
                : isEditMode 
                  ? `Сохранить изменения ${refundAmount || '0'} ₽`
                  : `Оформить возврат ${refundAmount || '0'} ₽`
              }
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Диалог подтверждения удаления возврата */}
      <AlertDialog open={!!deleteConfirmation} onOpenChange={(open) => !open && setDeleteConfirmation(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Удаление возврата
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 pt-4">
                {deleteConfirmation && (
                  <>
                    <div className="bg-destructive/10 border border-destructive/30 p-4 rounded-lg space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Сумма возврата:</span>
                        <span className="font-bold text-destructive">{deleteConfirmation.amount} ₽</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Дата:</span>
                        <span className="text-sm">{new Date(deleteConfirmation.payment_date).toLocaleString('ru-RU')}</span>
                      </div>
                      {deleteConfirmation.notes && (
                        <div className="pt-2 border-t border-destructive/20">
                          <span className="text-muted-foreground text-sm">Причина:</span>
                          <p className="text-sm mt-1">{deleteConfirmation.notes}</p>
                        </div>
                      )}
                    </div>

                    <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-4 rounded-lg space-y-2">
                      <p className="text-sm font-semibold text-amber-900 dark:text-amber-100 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Внимание!
                      </p>
                      <ul className="text-sm text-amber-800 dark:text-amber-200 space-y-1 ml-6 list-disc">
                        <li>Запись о возврате будет безвозвратно удалена</li>
                        <li>Баланс бронирования увеличится на {deleteConfirmation.amount} ₽</li>
                        <li>Финансовые показатели будут пересчитаны</li>
                        <li>Отменить это действие будет невозможно</li>
                      </ul>
                    </div>

                    <div className="bg-destructive/10 border border-destructive/20 p-3 rounded-lg">
                      <p className="text-sm text-destructive font-medium">
                        ⚠️ Вы уверены, что хотите удалить этот возврат?
                      </p>
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteRefund}
              disabled={isLoading}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isLoading ? 'Удаление...' : `Удалить возврат ${deleteConfirmation?.amount || '0'} ₽`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
